<script setup lang="ts">
import type { DataTableHeaders } from '@/plugins/vuetify'
import type MDBSemi from '@/types/ferme/semi'
import type MDBVegcoBucket from '@/types/vegco/bucket'
import { roundTo, toDate_wd_dd_MMMM, getSemiSuperficie, getSemiDateRécoltePrévue, getSemiVérifiéTextColor } from '@/utils/helpers'
import { unitésParBoîte } from '@/utils/helpers-vegco'

//https://stackoverflow.com/questions/57807226/vuetify-data-table-add-horizontal-stroller-bar

definePage({
  meta: {
    id: 'semis',
    title: 'Semis',
    icon: 'mdi-sprout-outline',
    drawerIndex: 1,
    requiresAuth: true,
    requiresSiteAccess: 'semis'
  },
})

const theme = useTheme()
const storeU = useUtilisateurStore()
const router = useRouter()
const isLoading = ref(true)
const révision = ref('')

const semis = ref([] as MDBSemi[])
const buckets = ref([] as MDBVegcoBucket[])
const search = ref('')
const headers = ref<DataTableHeaders>([
  { title: '#', key: 'formattedNuméro', width: '120px' },
  { title: 'Champ', key: 'champ', width: '70px' },
  { title: 'Planches', key: 'planches', width: '130px' },
  { title: 'Date récolte prévue', key: 'dateRécoltePrévue2', width: '180px' },
  { title: 'bts/ac prévu', key: 'rendementPrévuBoîte', width: '80px' },
  { title: 'Nb. boîtes', key: 'nbBoîtes', width: '80px' },
  { title: 'Sup.', key: 'formattedSuperficie', width: '60px' },
  { title: 'Degré-jour', key: 'djPct', width: '170px' },
  { title: 'Variété', key: 'formattedVariété', width: '110px' },
  { title: 'Date plantation', key: 'formattedDateDu', width: '120px' },
  { title: 'Commentaires', key: 'cmnts' },
  { title: 'Vérifié', key: 'dateVérification', width: '150px' },
  { title: 'Actions', key: 'actions', sortable: false, width: '120px' }
]);
const dialogColumns = ref(false)
const visibleColumns = ref(headers.value?.map(header => header.key).filter(key =>
  //key !== 'variété' &&
  (key !== 'djPct' || storeU.siteAAccès('degréjours'))
) ?? []);
const filteredHeaders = computed(() => headers.value?.filter(header => visibleColumns.value.includes(header.key)) ?? []);
const voirFermés = ref(false)
const showAjustementDJ = computed(() => storeU.siteAAccès('degréjours') &&
  (storeU.cultureSélectionnée.nom == 'Laitue Romaine' || storeU.cultureSélectionnée.nom == 'Iceberg / Pommée' || storeU.cultureSélectionnée.nom == 'Radis Chinois'))
const editAjustementDJ = ref(false)
const ajustementDJModifié = ref(0)
const isSavingAjustementDJ = ref(false)
const editDJNécessaireSemiId = ref<string | null>(null)
const savingDJNécessaireSemiId = ref<string | null>(null)
const djNécessaireManuelModifié = ref(0)
const unitParBoîte = computed(() => unitésParBoîte(storeU.cultureSélectionnée.nom))
const formattedSemis = computed(() => {
  let semisList = semis.value;

  if (!voirFermés.value) {
    semisList = semisList.filter(semi => !semi.fermé)
  }

  return semisList.map(semi => ({
    ...semi,
    formattedNuméro: semi.numéro + (semi.fermé ? ' (*)' : ''),
    formattedDateDu: toDate_wd_dd_MMMM(semi.dateDu),
    dateRécoltePrévue2: getSemiDateRécoltePrévue(semi),
    planches: semi.plancheDe + '-' + semi.plancheÀ + ' (' + (semi.plancheÀ - semi.plancheDe + 1) + ')',
    cmnts: getCommentaires(semi),
    formattedVariété: semi.variété + (semi.estBio ? ' (Bio)' : ''),
    rendementPrévuBoîte: semi.rendementPrévu / unitParBoîte.value,
    nbBoîtes: roundTo(getSemiSuperficie(semi) * semi.rendementPrévu / unitParBoîte.value, 0),
    formattedSuperficie: roundTo(getSemiSuperficie(semi), 2),
    djRéelAccumulé: roundTo(semi.degréJour?.djRéelAccumulé ?? 0, 0),
    djPct: semi.degréJour == null ? -1
      : semi.degréJour.complété ? 101
      : (semi.degréJour.djNécessaireCalculé == 0 && semi.degréJour.djNécessaireManuel == 0) ? -2
      : roundTo(semi.degréJour.djRéelAccumulé / (semi.degréJour.djNécessaireManuel !== 0 ? semi.degréJour.djNécessaireManuel : semi.degréJour.djNécessaireCalculé) * 100, 0),
  }));
});

function getDJColor(pct: number): string {
  if (pct >= 90) return 'success'
  if (pct >= 66) return 'orange'
  if (pct >= 33) return 'info'
  return 'primary'
}

function onAjustementDJCorbeilClick() {
  editAjustementDJ.value = false
  ajustementDJModifié.value = storeU.cultureSélectionnée.ajustementDJ
}

async function onAjustementDJPencilClick() {
  if (isSavingAjustementDJ.value)
    return

  editAjustementDJ.value = !editAjustementDJ.value

  if (storeU.cultureSélectionnée.ajustementDJ != ajustementDJModifié.value) {
    try {
      isSavingAjustementDJ.value = true
      const siteCS = storeU.siteSélectionné.lettresDappel
      const config = { params: { culture: storeU.cultureSélectionnée.nom, ajustement: ajustementDJModifié.value } }
      await apiProphet.put(`sites/${siteCS}/degrejours/ajustementdj`, null, config)
      Notify.success('Ajustement DJ modifié')
      storeU.cultureSélectionnée.ajustementDJ = ajustementDJModifié.value
      await getSemis()
    } catch (e) {
      Notify.error(e)
    } finally {
      isSavingAjustementDJ.value = false
    }
  }
}

function onDJNécessairePencilClick(item: MDBSemi) {
  editDJNécessaireSemiId.value = item.id

  djNécessaireManuelModifié.value = 0
  if (item.degréJour != null) {
    if (item.degréJour.djNécessaireManuel != 0) {
      djNécessaireManuelModifié.value = item.degréJour.djNécessaireManuel
    } else {
      djNécessaireManuelModifié.value = roundTo(item.degréJour.djNécessaireCalculé, 0)
    }

    if (!item.degréJour.complété) {
      djNécessaireManuelModifié.value += ajustementDJModifié.value
    }
  }
}

function onDJNécessaireCancelClick() {
  editDJNécessaireSemiId.value = null
}

// Quick rendement edit
const editRendementSemiId = ref<string | null>(null)
const rendementModifié = ref(0)

function onRendementPencilClick(item: any) {
  editRendementSemiId.value = item.id
  rendementModifié.value = item.rendementPrévuBoîte
  nextTick(() => rendementInputRef.value?.focus())
}

const rendementInputRef = ref<HTMLInputElement | null>(null)

function onRendementCancelClick() {
  editRendementSemiId.value = null
}

async function saveRendement(item: any) {
  const newRendement = rendementModifié.value * unitParBoîte.value
  const semiToUpdate = semis.value.find(s => s.id === item.id)
  if (!semiToUpdate) return
  if (semiToUpdate.rendementPrévu === newRendement) {
    editRendementSemiId.value = null
    return
  }
  try {
    const siteCS = storeU.siteSélectionné.lettresDappel
    await apiProphet.put(`sites/${siteCS}/semis/${item.id}/rendement`, null, { params: { rendement: newRendement } })
    semiToUpdate.rendementPrévu = newRendement
    Notify.success('Rendement modifié')
    editRendementSemiId.value = null
    await refreshRévision()
  } catch (e) {
    Notify.error(e)
  }
}

// Quick date récolte prévue edit
const editDateRécolteSemiId = ref<string | null>(null)
const dateRécolteModifiée = ref('')
const dateRécolteInputRef = ref<HTMLInputElement | null>(null)

function onDateRécoltePencilClick(item: any) {
  editDateRécolteSemiId.value = item.id
  // Format as YYYY-MM-DD for the date input
  const raw = item.dateRécoltePrévue2
  dateRécolteModifiée.value = raw ? raw.substring(0, 10) : ''
  nextTick(() => dateRécolteInputRef.value?.focus())
}

function onDateRécolteCancelClick() {
  editDateRécolteSemiId.value = null
}

async function saveDateRécolte(item: any) {
  if (editDateRécolteSemiId.value !== item.id) return
  editDateRécolteSemiId.value = null
  try {
    const siteCS = storeU.siteSélectionné.lettresDappel
    await apiProphet.put(`sites/${siteCS}/semis/${item.id}/daterecolteprevuemanuelle`, null, { params: { dateRecolte: dateRécolteModifiée.value ? dateRécolteModifiée.value + 'T12:00:00' : '0001-01-01' } })
    const semiToUpdate = semis.value.find(s => s.id === item.id)
    if (semiToUpdate)
      semiToUpdate.dateRécoltePrévueManuelle = dateRécolteModifiée.value ? dateRécolteModifiée.value + 'T12:00:00' : ''
    Notify.success('Date de récolte prévue modifiée')
    await refreshRévision()
  } catch (e) {
    Notify.error(e)
  }
}

async function saveDJNécessaireManuel(item: MDBSemi) {
  if (savingDJNécessaireSemiId.value === item.id) return

  const semiToUpdate = semis.value.find(s => s.id === item.id)
  if (!semiToUpdate || !semiToUpdate.degréJour) return

  let newValue = djNécessaireManuelModifié.value
  if (!semiToUpdate.degréJour.complété)
    newValue -= ajustementDJModifié.value
  if (newValue < 0)
    newValue = 0

  if (semiToUpdate.degréJour.djNécessaireManuel === newValue) {
    editDJNécessaireSemiId.value = null
    return
  }

  try {
    savingDJNécessaireSemiId.value = item.id
    const siteCS = storeU.siteSélectionné.lettresDappel
    const result = await apiProphet.put(`sites/${siteCS}/degrejours/miseajour/${item.id}`, null, {
      params: {
        djNecessaireManuel: newValue
      }
    })
    const semiApi = result.data as MDBSemi
    semiToUpdate.degréJour = semiApi.degréJour
    Notify.success('DJ nécessaire manuel modifié')
    editDJNécessaireSemiId.value = null
    await getSemis()
  } catch (e) {
    Notify.error(e)
  } finally {
    savingDJNécessaireSemiId.value = null
  }
}

function aDesBucketsAvecDesBalises(semi: MDBSemi) {
  return buckets.value.some(b => b.semiId == semi.id && b.balises.length > 0)
}

function getCommentaires(semi: MDBSemi) {
  let commentaires = ''

  if (semi.commentaires) {
    if (commentaires != '') commentaires += ' ||| '
    commentaires += semi.commentaires
  }

  if (semi.commentairesFarmit) {
    if (commentaires != '') commentaires += ' ||| '
    commentaires += semi.commentairesFarmit
  }

  for (const bucket of buckets.value.filter(b => b.semiId == semi.id)) {
    if (bucket.commentaires != undefined && bucket.commentaires != '') {
      if (commentaires != '') commentaires += ' ||| '
      commentaires += 'Bucket: ' + bucket.commentaires
    }
  }

  return commentaires
}

const totalSuperficie = computed(() => {
  return roundTo(semis.value.reduce((acc, semi) => acc + getSemiSuperficie(semi), 0), 2);
});

// Drag-selection for Sup. sum
const isDragging = ref(false)
const dragSelectedIds = ref<string[]>([])
const dragSelectionSum = computed(() => {
  if (dragSelectedIds.value.length === 0) return null
  const selected = semis.value.filter(s => dragSelectedIds.value.includes(s.id))
  return roundTo(selected.reduce((acc, s) => acc + getSemiSuperficie(s), 0), 2)
})

function startDrag(e: MouseEvent, item: any) {
  isDragging.value = true
  if (e.ctrlKey || e.metaKey) {
    // Ctrl+click: toggle individual row
    if (dragSelectedIds.value.includes(item.id))
      dragSelectedIds.value = dragSelectedIds.value.filter(id => id !== item.id)
    else
      dragSelectedIds.value = [...dragSelectedIds.value, item.id]
  } else {
    // Plain click / drag start: begin new selection
    dragSelectedIds.value = [item.id]
  }
}
function continueDrag(item: any) {
  if (!isDragging.value) return
  if (!dragSelectedIds.value.includes(item.id))
    dragSelectedIds.value = [...dragSelectedIds.value, item.id]
}
function endDrag() {
  isDragging.value = false
}
function getRowProps({ item }: { item: any }) {
  return {
    class: dragSelectedIds.value.includes(item.id) ? 'drag-row-selected' : '',
    style: 'user-select: none',
    onMousedown: (e: MouseEvent) => { e.preventDefault(); startDrag(e, item) },
    onMouseenter: () => continueDrag(item),
  }
}

function goToChampOnCarte(champ: string) {
  router.push({ path: '/carte', query: { champ } })
}

const dialogCulture = useTemplateRef('dialogCulture')
async function showDialogCulture() {
  const result = await dialogCulture.value?.open()
  if (result != undefined && result != '') {
    storeU.sélectionnerCulture(result, null)
    ajustementDJModifié.value = storeU.cultureSélectionnée.ajustementDJ
    await getSemis()
  } else {
    isLoading.value = false
  }
}

watch(() => storeU.siteSélectionné, async () => { await getSemis() })
watch(() => storeU.annéeSélectionnée, async () => { await getSemis() })

// Revision check
async function refreshRévision() {
  const siteCS = storeU.siteSélectionné.lettresDappel
  const result = await apiProphet.get(`sites/${siteCS}/revisions/semis`)
  révision.value = result.data
}

const intervalId = setInterval(async () => {
  const siteCS = storeU.siteSélectionné.lettresDappel
  if (storeU.cultureSélectionnée.nom == undefined || siteCS == undefined || siteCS == '') {
    return
  }
  const result = await apiProphet.get(`sites/${siteCS}/revisions/semis`)
  if (révision.value != result.data) {
    getSemis()
  }
}, 3000)

async function getSemis() {
  isLoading.value = true
  semis.value = []

  if (storeU.cultureSélectionnée.nom == undefined) {
    Notify.info('Veuillez sélectionner une culture avant de continuer.')
    isLoading.value = false
    return
  }

  try {
    ajustementDJModifié.value = storeU.cultureSélectionnée.ajustementDJ

    const siteCS = storeU.siteSélectionné.lettresDappel

    const result = await apiProphet.get(`sites/${siteCS}/revisions/semis`)
    révision.value = result.data

    const config = { params: { culture: storeU.cultureSélectionnée.nom, année: storeU.annéeSélectionnée } }
    const result2 = await apiProphet.get(`sites/${siteCS}/semis`, config)
    semis.value = result2.data

    const semiIds = semis.value.map((semi) => semi.id)
    const result3 = await apiProphet.post(`vgco/sites/${siteCS}/semis/multiple/buckets/commentaires`, semiIds)
    buckets.value = result3.data
  }
  catch (e) {
    Notify.error(e)
  }
  finally {
    isLoading.value = false
  }
}

const dialogSemi = useTemplateRef('dialogSemi')
async function _newSemi() {
  await dialogSemi.value?.openSemiNew()
  getSemis()
}
async function editSemi(semi: MDBSemi) {
  const modifs = await dialogSemi.value?.openSemiEdit(semi)
  if (modifs)
    getSemis()
}

/*const dialogDelete = useTemplateRef('dialogDelete')
async function deleteSemi(semi: MDBSemi) {
  if (semi.farmitId != undefined && semi.farmitId != '') {
    Notify.error('Impossible de supprimer un semi provenant de Farmit')
    return
  }

  const result = await dialogDelete.value?.open('Êtes-vous certain de vouloir supprimer ce semi ?');
  if (result?.valueOf()) {
    try {
      const siteCS = storeU.siteSélectionné.lettresDappel
      await apiProphet.delete(`sites/${siteCS}/semis/${semi.id}`)
      Notify.success('Supprimé')
      await getSemis()
    } catch (e) {
      Notify.error(e)
    }
  }
}*/

const dialogBuckets = useTemplateRef('dialogBuckets')
async function openBuckets(semi: MDBSemi) {
  const modifs = await dialogBuckets.value?.openBucketsAsync(semi, null)
  if (modifs) {
    getSemis()
  }
}

function getVérifiéColumnValue(dateVérification: string) {
  if (dateVérification == undefined || dateVérification == null || dateVérification == '')
    return 'Jamais'
  else if (new Date(dateVérification).getFullYear() > 2000)
    return toDate_wd_dd_MMMM(dateVérification)
  else
    return 'Jamais'
}

function print() {
  const printWindow = window.open('', '_blank')
  if (printWindow) {
    printWindow.document.open()
    printWindow.document.write(generateReportHTML())
    printWindow.document.close()
    //setTimeout(() => printWindow.print(), 500)
  }
}

function generateReportHTML() {
  const printableHeaders = filteredHeaders.value.filter(h => h.key !== 'actions')
  const rows = formattedSemis.value

  function getCellValue(key: string, item: any): string {
    switch (key) {
      case 'dateRécoltePrévue2':
        return item.dateRécoltePrévue2 == '' ? 'Aucune date' : toDate_wd_dd_MMMM(item.dateRécoltePrévue2)
      case 'dateVérification':
        return getVérifiéColumnValue(item.dateVérification)
      case 'djPct': {
        if (item.degréJour == null) return '⚠ Erreur'
        const djAccumulé = roundTo(item.degréJour.djRéelAccumulé, 0)
        const djNécessaire = roundTo(
          (item.degréJour.djNécessaireManuel != 0 ? item.degréJour.djNécessaireManuel : item.degréJour.djNécessaireCalculé)
          + (item.degréJour.complété ? 0 : ajustementDJModifié.value), 0)
        if (item.degréJour.complété)
          return `✓ ${djAccumulé} / ${djNécessaire}`
        if (item.degréJour.djNécessaireCalculé == 0 && item.degréJour.djNécessaireManuel == 0)
          return `${djAccumulé} / ⚠`
        const pct = roundTo(item.degréJour.djRéelAccumulé / item.degréJour.djNécessaireCalculé * 100, 0)
        return `${djAccumulé} / ${djNécessaire} (${pct}%)`
      }
      default:
        return String(item[key] ?? '')
    }
  }

  const now = new Date()
  const dateStr = `${now.toLocaleDateString('fr-CA')} ${now.toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}`

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Semis - ${storeU.cultureSélectionnée.nom}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
        h3 { margin-bottom: 4px; }
        p { margin: 0 0 12px; }
        table { border-collapse: collapse; width: 100%; }
        th { background-color: #f2f2f2; font-weight: bold; border: 1px solid #999; padding: 4px 6px; text-align: left; white-space: nowrap; }
        td { border: 1px solid #ccc; padding: 4px 6px; text-align: left; }
        tr:nth-child(even) td { background-color: #fafafa; }
        .fermé td { color: #999; }
      </style>
    </head>
    <body>
      <h3>Semis — ${storeU.cultureSélectionnée.nom} ${storeU.annéeSélectionnée}</h3>
      <p>Total superficie : ${totalSuperficie.value}ac — Généré le : ${dateStr}</p>
      <table>
        <thead>
          <tr>
            ${printableHeaders.map(h => `<th>${h.title}</th>`).join('')}
          </tr>
        </thead>
        <tbody>
          ${rows.map(item => `
            <tr class="${item.fermé ? 'fermé' : ''}">
              ${printableHeaders.map(h => `<td>${getCellValue(String(h.key), item)}</td>`).join('')}
            </tr>
          `).join('')}
        </tbody>
      </table>
    </body>
    </html>
  `
}

onMounted(async () => {
  window.addEventListener('mouseup', endDrag)
  if (storeU.cultureSélectionnée.id == undefined) {
    showDialogCulture()
  } else {
    await getSemis()
  }
})

onUnmounted(() => {
  window.removeEventListener('mouseup', endDrag)
  if (intervalId)
    clearInterval(intervalId)
})
</script>

<template>
  <v-card class="mx-4 my-4 page-card" rounded="xl" :loading="isLoading">

    <!-- Page header -->
    <div class="page-header px-5 pt-5 pb-4">

      <!-- Desktop -->
      <div v-if="$vuetify.display.mdAndUp" class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">
        <!-- Left: title + culture + year -->
        <div class="d-flex align-center" style="gap: 12px;">
          <v-avatar color="primary" variant="tonal" size="44" rounded="lg">
            <v-icon icon="mdi-sprout-outline" size="26"/>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-bold" style="line-height: 1.2;">Semis</div>
            <div class="text-caption text-medium-emphasis">Plantation par champ</div>
          </div>
          <v-chip
            color="primary" variant="tonal" size="large" class="ms-1 culture-chip"
            append-icon="mdi-chevron-down" style="cursor: pointer; font-weight: 600;"
            @click="showDialogCulture">
            {{ storeU.cultureSélectionnée.nom ?? 'Culture...' }}
          </v-chip>
          <YearSelector/>
        </div>
        <!-- Right: stats + controls -->
        <div class="d-flex align-center flex-wrap" style="gap: 8px;">
          <span class="text-body-2 text-medium-emphasis">
            Total : <strong>{{ totalSuperficie }}ac</strong>
          </span>
          <v-chip
            v-if="dragSelectionSum !== null"
            closable color="blue" variant="tonal" size="small"
            @click:close="dragSelectedIds = []"
          >Sél. : {{ dragSelectionSum }}ac ({{ dragSelectedIds.length }} semis)</v-chip>
          <v-checkbox v-model="voirFermés" label="Fermés" class="ma-0 pa-0" hide-details density="compact"/>
          <v-divider vertical style="height: 28px; align-self: center;"/>
          <v-text-field
            v-model="search" prepend-inner-icon="mdi-magnify" label="Recherche"
            density="comfortable" rounded="pill" variant="solo-filled" single-line hide-details flat
            style="min-width: 280px; max-width: 400px; flex: 1 1 280px;"
          />
          <v-btn color="primary" variant="tonal" rounded="pill" size="small" icon="mdi-eye" @click="dialogColumns = true"/>
          <v-btn color="primary" variant="tonal" rounded="pill" size="small" icon="mdi-printer" @click="print()"/>
          <template v-if="showAjustementDJ">
            <v-divider vertical style="height: 28px; align-self: center;"/>
            <v-text-field
              v-if="!editAjustementDJ" v-model.number="ajustementDJModifié" label="Ajust. DJ" type="number"
              readonly :disabled="isSavingAjustementDJ" density="compact" variant="outlined" rounded="pill" hide-details style="max-width: 130px"
              append-inner-icon="mdi-pencil" @click:append-inner="onAjustementDJPencilClick"/>
            <v-text-field
              v-else v-model.number="ajustementDJModifié" label="Ajust. DJ" type="number"
              :disabled="isSavingAjustementDJ" density="compact" variant="outlined" rounded="pill" hide-details style="max-width: 160px"
              append-inner-icon="mdi-check" append-icon="mdi-trash-can"
              @click:append="onAjustementDJCorbeilClick" @click:append-inner="onAjustementDJPencilClick"/>
            <v-progress-circular
              v-if="isSavingAjustementDJ"
              indeterminate size="18" width="2" color="primary"/>
          </template>
        </div>
      </div>

      <!-- Mobile -->
      <template v-else>
        <div class="d-flex align-center justify-space-between mb-3">
          <div class="d-flex align-center" style="gap: 10px;">
            <v-avatar color="primary" variant="tonal" size="38" rounded="lg">
              <v-icon icon="mdi-sprout-outline" size="22"/>
            </v-avatar>
            <div>
              <div class="text-subtitle-1 font-weight-bold" style="line-height: 1.2;">Semis</div>
              <div class="text-caption text-medium-emphasis">{{ storeU.annéeSélectionnée }}</div>
            </div>
          </div>
          <div class="d-flex align-center" style="gap: 6px;">
            <v-chip
              color="primary" variant="tonal" size="small" class="culture-chip"
              append-icon="mdi-chevron-down" style="cursor: pointer; font-weight: 600;"
              @click="showDialogCulture">
              {{ storeU.cultureSélectionnée.nom ?? 'Culture...' }}
            </v-chip>
            <YearSelector/>
          </div>
        </div>
        <div class="d-flex align-center flex-wrap" style="gap: 8px;">
          <span class="text-caption text-medium-emphasis">
            Total : <strong>{{ totalSuperficie }}ac</strong>
          </span>
          <v-chip
            v-if="dragSelectionSum !== null"
            closable color="blue" variant="tonal" size="x-small"
            @click:close="dragSelectedIds = []"
          >Sél. : {{ dragSelectionSum }}ac</v-chip>
          <v-checkbox v-model="voirFermés" label="Fermés" class="ma-0 pa-0" hide-details density="compact"/>
          <v-spacer/>
          <v-btn color="primary" variant="tonal" rounded="pill" size="small" icon="mdi-printer" @click="print()"/>
        </div>
        <v-text-field
          v-model="search" prepend-inner-icon="mdi-magnify" label="Rechercher un semi..."
          density="comfortable" rounded="pill" variant="solo-filled" single-line hide-details flat
          class="mt-3"
        />
      </template>

    </div>

    <v-divider/>

    <v-data-table
      :headers="filteredHeaders" :items="formattedSemis" :items-per-page="-1"
      :loading="isLoading" :search="search" item-key="id"
      :sort-by="[{ key: 'numéro', order: 'asc' }]" :sort-desc="false"
      :hide-default-header="$vuetify.display.smAndDown"
      :row-props="getRowProps">
      <template #item.formattedNuméro="{ value }">
        <v-chip color="primary" size="small" variant="flat" class="font-weight-bold">{{ value }}</v-chip>
      </template>
      <template #item.champ="{ value }">
        <v-chip color="teal" size="small" variant="tonal" class="font-weight-bold" style="cursor:pointer;" @click.stop="goToChampOnCarte(value)" @mousedown.stop>
          <v-icon start size="x-small">mdi-map-marker</v-icon>
          {{ value }}
        </v-chip>
      </template>
      <template #item.planches="{ value }">
        <v-chip color="brown-lighten-1" size="small" variant="tonal" class="font-weight-bold">
          <v-icon start size="x-small">mdi-land-rows-horizontal</v-icon>
          {{ value }}
        </v-chip>
      </template>
      <template #item.dateRécoltePrévue2="{ value, item }">
        <template v-if="editDateRécolteSemiId !== item.id">
          <div class="rendement-cell d-inline-flex align-center">
            <v-chip
              color="green-darken-1" variant="tonal" size="small" style="cursor:text"
              @click="onDateRécoltePencilClick(item)" @mousedown.stop
            >
              <v-icon start size="x-small">mdi-calendar-check</v-icon>
              {{ value == '' ? 'Aucune date' : toDate_wd_dd_MMMM(value) }}
            </v-chip>
            <v-icon class="rendement-pencil ml-1" size="x-small" @click="onDateRécoltePencilClick(item)" @mousedown.stop>mdi-pencil</v-icon>
          </div>
        </template>
        <template v-else>
          <div class="d-flex align-center" style="gap:4px">
            <input
              ref="dateRécolteInputRef"
              v-model="dateRécolteModifiée"
              type="date"
              class="date-inline-input"
              @keyup.enter="saveDateRécolte(item)"
              @keyup.escape="onDateRécolteCancelClick"
              @blur="saveDateRécolte(item)"
              @mousedown.stop
              @click.stop
            />
          </div>
        </template>
      </template>
      <template #item.rendementPrévuBoîte="{ value, item }">
        <div class="d-flex align-center rendement-cell">
          <input
            v-if="editRendementSemiId === item.id"
            ref="rendementInputRef"
            v-model.number="rendementModifié"
            type="number"
            class="rendement-inline-input font-weight-bold text-primary no-spinner"
            @keyup.enter="saveRendement(item)"
            @keyup.escape="onRendementCancelClick"
            @blur="saveRendement(item)"
            @mousedown.stop
            @click.stop
          />
          <span v-else class="font-weight-bold text-primary" style="cursor:text" @click="onRendementPencilClick(item)" @mousedown.stop>{{ value }}</span>
          <v-icon
            class="rendement-pencil ml-1" size="x-small"
            @click="onRendementPencilClick(item)"
            @mousedown.stop
          >mdi-pencil</v-icon>
        </div>
      </template>
      <template #item.nbBoîtes="{ value }">
        <span class="font-weight-bold text-success">{{ value }}</span>
      </template>
      <template #item.dateVérification="{ item }">
        <span :class="getSemiVérifiéTextColor(item)">{{ getVérifiéColumnValue(item.dateVérification) }}</span><br/>
        <v-chip v-if="!aDesBucketsAvecDesBalises(item)" size="x-small" color="error" variant="flat" class="mt-1">
          <v-icon start size="x-small">mdi-alert</v-icon>
          Pas de balises
        </v-chip>
      </template>
      <template v-if="$vuetify.display.smAndDown" #item="props">
        <tr class="mobile-semi-row">
          <td class="pa-2" style="border-bottom: none;">
            <v-card
              class="semi-mobile-card"
              :class="props.item.fermé ? 'semi-mobile-card--fermé' : ''"
              :elevation="props.item.fermé ? 0 : 2"
              rounded="lg"
            >
              <!-- Header: # + Champ + Actions -->
              <div class="d-flex align-center justify-space-between px-3 pt-3 pb-1">
                <div class="d-flex align-center flex-wrap" style="gap: 6px;">
                  <v-chip color="primary" size="small" variant="flat" class="font-weight-bold">
                    # {{ props.item.formattedNuméro }}
                  </v-chip>
                  <v-chip color="teal" size="small" variant="tonal" class="font-weight-bold" style="cursor:pointer;" @click.stop="goToChampOnCarte(props.item.champ)" @mousedown.stop>
                    <v-icon start size="x-small">mdi-map-marker</v-icon>
                    {{ props.item.champ }}
                  </v-chip>
                  <v-chip color="brown-lighten-1" size="small" variant="tonal" class="font-weight-bold">
                    <v-icon start size="x-small">mdi-land-rows-horizontal</v-icon>
                    {{ props.item.planches }}
                  </v-chip>
                  <v-chip v-if="props.item.fermé" color="grey" size="x-small" variant="outlined">Fermé</v-chip>
                </div>
                <div class="d-flex">
                  <v-btn icon variant="text" size="small" density="compact" @click="editSemi(props.item)">
                    <v-icon size="20">mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn icon variant="text" size="small" density="compact" @click="openBuckets(props.item)">
                    <v-icon size="20">mdi-bucket-outline</v-icon>
                  </v-btn>
                </div>
              </div>

              <!-- Date de récolte — accent info -->
              <div class="px-3 pb-2 d-flex align-center">
                <v-icon size="15" color="green-darken-1" class="mr-1">mdi-calendar-check</v-icon>
                <span class="text-caption text-medium-emphasis mr-1">Récolte prévue :</span>
                <template v-if="editDateRécolteSemiId === props.item.id">
                  <input
                    ref="dateRécolteInputRef"
                    v-model="dateRécolteModifiée"
                    type="date"
                    class="date-inline-input"
                    @keyup.enter="saveDateRécolte(props.item)"
                    @keyup.escape="onDateRécolteCancelClick"
                    @blur="saveDateRécolte(props.item)"
                    @mousedown.stop
                    @click.stop
                  />
                </template>
                <template v-else>
                  <span
                    class="text-body-2 font-weight-medium"
                    style="cursor:text"
                    @click="onDateRécoltePencilClick(props.item)"
                  >
                    {{ props.item.dateRécoltePrévue2 === '' ? 'Aucune date' : toDate_wd_dd_MMMM(props.item.dateRécoltePrévue2) }}
                  </span>
                  <v-icon class="ml-1" size="x-small" color="grey" @click="onDateRécoltePencilClick(props.item)">mdi-pencil</v-icon>
                </template>
              </div>

              <v-divider/>

              <!-- Key metrics: rendement / nb boîtes / superficie -->
              <div class="d-flex justify-space-around py-3">
                <div class="text-center">
                  <div class="rendement-cell rendement-cell--always-show d-inline-flex align-center justify-center">
                    <input
                      v-if="editRendementSemiId === props.item.id"
                      ref="rendementInputRef"
                      v-model.number="rendementModifié"
                      type="number"
                      inputmode="numeric"
                      pattern="[0-9]*"
                      class="rendement-inline-input font-weight-bold text-primary no-spinner text-h6"
                      style="width:5ch;text-align:center"
                      @keyup.enter="saveRendement(props.item)"
                      @keyup.escape="onRendementCancelClick"
                      @blur="saveRendement(props.item)"
                      @mousedown.stop
                      @click.stop
                    />
                    <span v-else class="text-h6 font-weight-bold text-primary" style="cursor:text" @click="onRendementPencilClick(props.item)" @mousedown.stop>{{ props.item.rendementPrévuBoîte }}</span>
                    <v-icon
                      class="rendement-pencil ml-1" size="x-small"
                      @click="onRendementPencilClick(props.item)"
                      @mousedown.stop
                    >mdi-pencil</v-icon>
                  </div>
                  <div class="text-caption text-disabled">Rend./ac</div>
                </div>
                <v-divider vertical/>
                <div class="text-center">
                  <div class="text-h6 font-weight-bold text-success">{{ props.item.nbBoîtes }}</div>
                  <div class="text-caption text-disabled">Boîtes</div>
                </div>
                <v-divider vertical/>
                <div class="text-center">
                  <div class="text-h6 font-weight-bold">{{ props.item.formattedSuperficie }}</div>
                  <div class="text-caption text-disabled">Acres</div>
                </div>
              </div>

              <v-divider/>

              <!-- DJ progress (if available) -->
              <div v-if="props.item.degréJour != null && storeU.siteAAccès('degréjours')" class="px-3 pt-2 pb-1">
                <template v-if="props.item.degréJour.complété">
                  <v-progress-linear :model-value="100" color="grey" rounded height="14" bg-color="grey-lighten-1">
                    <span class="text-caption font-weight-bold">✓ Récolté</span>
                  </v-progress-linear>
                </template>
                <template v-else-if="props.item.degréJour.djNécessaireCalculé == 0 && props.item.degréJour.djNécessaireManuel == 0">
                  <v-progress-linear :model-value="0" color="warning" rounded height="14" bg-color="grey-lighten-1">
                    <span class="text-caption font-weight-bold">⚠ Cible DJ inconnue</span>
                  </v-progress-linear>
                </template>
                <template v-else>
                  <v-progress-linear
                    :model-value="Math.min(props.item.djPct, 100)"
                    :color="getDJColor(props.item.djPct)"
                    rounded height="14" bg-color="grey-lighten-1"
                  >
                    <span class="text-caption font-weight-bold dj-bar-text">{{ props.item.djPct }}% DJ</span>
                  </v-progress-linear>
                </template>
              </div>

              <!-- Secondary info -->
              <div class="px-3 py-2 text-caption">
                <div class="d-flex flex-wrap" style="gap: 2px 12px;">
                  <span><span class="text-medium-emphasis">Variété :</span> {{ props.item.formattedVariété }}</span>
                  <span><span class="text-medium-emphasis">Plantation :</span> {{ props.item.formattedDateDu }}</span>
                </div>
                <div v-if="props.item.cmnts" class="mt-1 text-medium-emphasis semi-cmnt-clamp">
                  <v-icon size="11" class="mr-1">mdi-comment-text-outline</v-icon>{{ props.item.cmnts }}
                </div>
              </div>

              <!-- Footer: vérification + balises -->
              <v-divider/>
              <div class="px-3 py-2 d-flex align-center justify-space-between">
                <span :class="['text-caption', getSemiVérifiéTextColor(props.item)]">
                  <v-icon size="12" class="mr-1">mdi-check-decagram</v-icon>
                  Vérifié : {{ getVérifiéColumnValue(props.item.dateVérification) }}
                </span>
                <v-chip v-if="!aDesBucketsAvecDesBalises(props.item)" size="x-small" color="error" variant="flat">
                  <v-icon start size="x-small">mdi-alert</v-icon>
                  Pas de balises
                </v-chip>
              </div>
            </v-card>
          </td>
        </tr>
      </template>
      <template #item.formattedVariété="{ value, item }">
        <span>{{ value }}</span>
        <span v-if="item.estBio" class="text-caption">
          <v-icon size="small" color="green">mdi-leaf</v-icon>
        </span>
      </template>
      <template #item.djPct="{ item }">
        <!-- No DJ system -->
        <span v-if="item.degréJour == null">
          <v-icon>mdi-alert-decagram</v-icon>
          <v-tooltip activator="parent" location="bottom">
            N'a pas pu créer le système de degré-jour<br/>
            (Pas de date de récolte dans le passé ?)
          </v-tooltip>
        </span>
        <!-- Has DJ system -->
        <div v-else style="min-width:150px">
          <!-- Completed -->
          <template v-if="item.degréJour.complété">
            <v-progress-linear :model-value="100" color="grey" rounded height="18" bg-color="grey-lighten-1">
              <span class="text-caption font-weight-bold">✓ Récolté</span>
            </v-progress-linear>
          </template>
          <!-- No target -->
          <template v-else-if="item.degréJour.djNécessaireCalculé == 0 && item.degréJour.djNécessaireManuel == 0">
            <v-progress-linear :model-value="0" color="warning" rounded height="18" bg-color="grey-lighten-1">
              <span class="text-caption font-weight-bold">⚠ Cible inconnue</span>
            </v-progress-linear>
            <v-tooltip activator="parent" location="bottom">
              N'a pas pu trouvé de degré-jour nécessaire<br/>
              (Pas de données historique de degré-jour pour cette culture ?)
            </v-tooltip>
          </template>
          <!-- Normal progress -->
          <template v-else>
            <v-progress-linear
              :model-value="Math.min(item.djPct, 100)"
              :color="getDJColor(item.djPct)"
              rounded height="18" bg-color="grey-lighten-1"
            >
              <span class="text-caption font-weight-bold dj-bar-text">{{ item.djPct }}%</span>
            </v-progress-linear>
          </template>
          <!-- Accumulated / target row -->
          <div class="d-flex align-center mt-1 text-caption">
            <span>{{ roundTo(item.degréJour.djRéelAccumulé, 0) }}</span>
            <span class="mx-1">/</span>
            <template v-if="editDJNécessaireSemiId !== item.id">
              <span>{{ roundTo(item.degréJour.djNécessaireManuel != 0 ? item.degréJour.djNécessaireManuel : item.degréJour.djNécessaireCalculé, 0) + (item.degréJour.complété ? 0 : ajustementDJModifié) }}</span>
              <v-icon class="ml-1" size="x-small" @click="onDJNécessairePencilClick(item)" @mousedown.stop>mdi-pencil</v-icon>
            </template>
            <template v-else>
              <v-text-field
                v-model.number="djNécessaireManuelModifié" type="number"
                density="compact" variant="outlined" hide-details
                :disabled="savingDJNécessaireSemiId === item.id"
                style="display:inline-block;max-width:80px;vertical-align:middle;"
                @keyup.enter="saveDJNécessaireManuel(item)"
                @mousedown.stop
              />
              <v-progress-circular
                v-if="savingDJNécessaireSemiId === item.id"
                class="ml-2"
                indeterminate
                size="18"
                width="2"
                color="primary"
              />
              <template v-else>
                <v-icon class="ml-1" size="small" color="success" @click="saveDJNécessaireManuel(item)" @mousedown.stop>mdi-check</v-icon>
                <v-icon size="small" color="error" @click="onDJNécessaireCancelClick" @mousedown.stop>mdi-close</v-icon>
              </template>
            </template>
          </div>
        </div>
      </template>
      <template #item.actions="{ item }">
        <v-btn icon variant="text" size="small" density="compact" @click="editSemi(item)" @mousedown.stop>
          <v-icon size="18">mdi-pencil</v-icon>
        </v-btn>
        <v-btn icon variant="text" size="small" density="compact" @click="openBuckets(item)" @mousedown.stop>
          <v-icon size="18">mdi-bucket-outline</v-icon>
        </v-btn>
      </template>
    </v-data-table>
  </v-card>

  <DialogCulture ref='dialogCulture'/>
  <DialogConfirm ref="dialogDelete"/>
  <DialogColonnes ref='dialogColonnes' v-model="dialogColumns" v-model:headers="headers" v-model:visibleheaders="visibleColumns" />
  <DialogSemi ref='dialogSemi'/>
  <DialogBuckets ref="dialogBuckets"/>
</template>

<style scoped>
/* ── Page header ─────────────────────────── */
.page-header {
  background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
}

/* ── Culture chip hover ───────────────────── */
.culture-chip {
  transition: opacity 0.15s;
}
.culture-chip:hover {
  opacity: 0.78;
}

:deep(.drag-row-selected td) {
  background-color: rgba(33, 150, 243, 0.15) !important;
}
:deep(.no-spinner input[type=number]::-webkit-inner-spin-button),
:deep(.no-spinner input[type=number]::-webkit-outer-spin-button) {
  -webkit-appearance: none;
  margin: 0;
}
:deep(.no-spinner input[type=number]) {
  -moz-appearance: textfield;
}
:deep(.rendement-edit-field .v-field__append-inner) {
  padding-top: 0;
  align-items: center;
  gap: 2px;
}
.rendement-inline-input {
  width: 4.5ch;
  background: transparent;
  border: none;
  border-bottom: 1.5px solid currentColor;
  outline: none;
  font-size: inherit;
  font-family: inherit;
  line-height: inherit;
  padding: 0;
  color: rgb(var(--v-theme-primary));
  -moz-appearance: textfield;
}
.rendement-inline-input::-webkit-inner-spin-button,
.rendement-inline-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.date-inline-input {
  background: transparent;
  border: none;
  border-bottom: 1.5px solid rgb(var(--v-theme-primary));
  outline: none;
  font-size: inherit;
  font-family: inherit;
  color: inherit;
  padding: 0;
  cursor: pointer;
}
.rendement-cell:not(:hover) .rendement-pencil {
  opacity: 0;
}
.rendement-cell:hover .rendement-pencil,
.rendement-cell--always-show .rendement-pencil {
  opacity: 0.6;
}
.dj-bar-text {
  color: #1a1a1a;
  text-shadow:
    -1px -1px 0 rgba(255,255,255,0.9),
     1px -1px 0 rgba(255,255,255,0.9),
    -1px  1px 0 rgba(255,255,255,0.9),
     1px  1px 0 rgba(255,255,255,0.9);
}

/* Mobile card styles */
:deep(.mobile-semi-row) {
  background: transparent !important;
}
:deep(.mobile-semi-row:hover td) {
  background: transparent !important;
}
.semi-mobile-card {
  border-left: 3px solid rgb(var(--v-theme-primary));
  transition: box-shadow 0.2s;
}
.semi-mobile-card--fermé {
  border-left-color: #bdbdbd;
  opacity: 0.75;
}
.semi-cmnt-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
